import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { environment } from '@environments/environment';
import { DateSelection } from '@models/date-selection';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { NavigationService } from '@services/navigation.service';
import { TreatmentTypeService } from '@services/treatment-type.service';

@Injectable({
  providedIn: 'root',
})
export class AgeRestrictedUpsellGuard implements CanActivate {
  private router = inject(Router);
  private consultationStorageService = inject(ConsultationStorageService);
  private navigationService = inject(NavigationService);
  private treatmentTypeService = inject(TreatmentTypeService);

  public async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    if (!this.isAgeRestricted(route.data?.ageRestrictedTreatmentType)) {
      return true;
    }

    const nextPageUrl = await this.navigationService.getConsultationRequestNextPageUrl(
      this.consultationStorageService.treatmentType,
      state.url,
    );

    return this.router.createUrlTree([nextPageUrl]);
  }

  /**
   * Determines if the current URL is restricted based on the patient's age.
   *
   * @param {ConsultationTreatmentTypes} treatmentType
   *
   * @returns {boolean} true if the current URL is restricted for the patient's gender, false otherwise
   */
  private isAgeRestricted(treatmentType?: ConsultationTreatmentTypes): boolean {
    const minAgeAllowed = this.treatmentTypeService.getTreatmentType(treatmentType)?.minAgeAllowed;
    const birthday = this.consultationStorageService.patient?.birthday;

    if (!minAgeAllowed || !birthday) {
      return false;
    }

    const { day, month, year } = birthday;

    return !new DateSelection(+month, +day, +year).isAgeValid(minAgeAllowed, environment.maxAgeAllowed);
  }
}
